// WhyDeepcBento — bento-style alternative for the partnership section.
// Each card carries a small visual element. Structured so hover animations
// can be added next iteration via group-hover utilities.
import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import Reveal from "./Reveal";
import Brand from "./Brand";
import PartnershipTimeline from "./visuals/PartnershipTimeline";
import WeeksToProduction from "./visuals/WeeksToProduction";
import TeamMatrix from "./visuals/TeamMatrix";
import AdoptionCurve from "./visuals/AdoptionCurve";

const easeOut = [0.22, 1, 0.36, 1] as const;

type Row = {
  eyebrow: string;
  title: string;
  body: string;
  caption: string;
};

const rows: Row[] = [
  {
    eyebrow: "Strategy",
    title: "Infrastructure that doesn't become technical debt",
    body:
      "Clinical AI isn't a single procurement decision — it's a multi-year institutional capability. We work with your leadership to plan for governance, regulatory shifts, and the models you haven't bought yet.",
    caption: "Strategic partnership, not vendor delivery",
  },
  {
    eyebrow: "Speed",
    title: "We deliver value in weeks, not months.",
    body:
      "Most partners go from contract to first model in production in under six weeks. Our deployment patterns are stress tested across leading academic centers, national health systems, and multi-site networks.",
    caption: "Time-to-value measured in weeks",
  },
  {
    eyebrow: "Embedded",
    title: "We're embedded with your team.",
    body:
      "Clinical AI fails at the seams between departments. deepc brings a team of experts who work alongside clinical, IT, management, compliance, and procurement to make AI operationally real.",
    caption: "Multidisciplinary collaboration",
  },
  {
    eyebrow: "Adoption",
    title: "Make AI value measurable.",
    body:
      "deepc tracks how AI workflows move from first use to full deployment - measuring adoption, usage, performance signals, and operational impact along the way. Clinical, technical, and executive teams get the visibility to understand which workflows are scaling, where attention is needed, and whether AI is creating value in routine care.",
    caption: "Adoption + ROI realization",
  },
];

/* ---------------- Visual elements ---------------- */
// Minimal, distinct visuals. Brand color used once per visual as a focal accent.

const VisualFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="relative h-[160px] md:h-[180px] w-full flex items-center justify-center">
    {children}
  </div>
);

// Inverted pyramid of collaboration layers — narrowest (most specialized) on top,
// widest (foundational research network) at the base. Uses primary with graded opacity.
const CollaborationStack = () => {
  const tiers = [
    { label: "Workflow & Value",          width: 50, opacity: 0.12 },
    { label: "Delivery & Implementation", width: 62, opacity: 0.28 },
    { label: "Management & Operations",   width: 75, opacity: 0.45 },
    { label: "Regulatory & Compliance",   width: 88, opacity: 0.62 },
    { label: "Research Network",          width: 100, opacity: 0.85 },
  ];
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const reduce = useReducedMotion();
  const show = inView || reduce;

  // Highlight relay: top → bottom, one tier active at a time, looping.
  const SLOT = 0.9;
  const FADE = 0.15;
  const CYCLE = tiers.length * SLOT; // 4.5s
  const RELAY_START = 1.3;
  // Each tier's "on" window centered on its slot.
  const buildHighlight = (i: number) => {
    const onStart = i * SLOT;
    const onPeak = onStart + FADE;
    const offPeak = onStart + SLOT - FADE;
    const offEnd = onStart + SLOT;
    const times = [
      0,
      onStart / CYCLE,
      onPeak / CYCLE,
      offPeak / CYCLE,
      offEnd / CYCLE,
      1,
    ];
    return { times };
  };

  return (
    <div ref={ref} className="w-full space-y-3">
      {tiers.map((t, i) => {
        // Bottom-up reveal: last tier first
        const order = tiers.length - 1 - i;
        const buildDelay = reduce ? 0 : order * 0.11;
        const baseBg = `hsl(var(--primary) / ${t.opacity})`;
        const hotBg = `hsl(var(--primary) / ${Math.min(0.95, t.opacity * 1.6 + 0.15)})`;
        const { times } = buildHighlight(i);
        const highlightProps =
          reduce || !show
            ? {}
            : {
                animate: {
                  backgroundColor: [baseBg, baseBg, hotBg, hotBg, baseBg, baseBg],
                  borderRightWidth: ["2px", "2px", "3px", "3px", "2px", "2px"],
                  boxShadow: [
                    "0 0 0 hsl(var(--primary) / 0)",
                    "0 0 0 hsl(var(--primary) / 0)",
                    "0 0 14px hsl(var(--primary) / 0.55)",
                    "0 0 14px hsl(var(--primary) / 0.55)",
                    "0 0 0 hsl(var(--primary) / 0)",
                    "0 0 0 hsl(var(--primary) / 0)",
                  ],
                },
                transition: {
                  duration: CYCLE,
                  times,
                  ease: "easeInOut" as const,
                  repeat: Infinity,
                  delay: RELAY_START,
                },
              };
        return (
          <div key={t.label} className="flex justify-start">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={show ? { scaleX: 1 } : {}}
              transition={{ duration: reduce ? 0 : 0.6, ease: easeOut, delay: buildDelay }}
              className="relative flex items-center justify-end h-11 md:h-12 pr-4 border-r-2 border-primary origin-left"
              style={{
                width: `${t.width}%`,
                backgroundColor: baseBg,
              }}
            >
              <motion.div
                aria-hidden
                {...highlightProps}
                className="absolute inset-0 border-r-2 border-primary pointer-events-none"
              />
              <motion.span
                initial={{ opacity: 0 }}
                animate={show ? { opacity: 1 } : {}}
                transition={{ duration: reduce ? 0 : 0.35, ease: easeOut, delay: buildDelay + 0.3 }}
                className="relative font-mono text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-foreground text-right"
              >
                {t.label}
              </motion.span>
            </motion.div>
          </div>
        );
      })}
    </div>
  );
};


// Gantt-style timeline for Card 02 (Time-to-Value).
const GanttTimeline = () => {
  const tasks = [
    { label: "Discovery", left: 0, width: 100 / 3 },
    { label: "Integration", left: 100 / 6, width: 50 },
    { label: "Validation", left: 50, width: 100 / 3 },
    { label: "Go-live", left: (100 * 5) / 6, width: 100 / 6 },
  ];
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const reduce = useReducedMotion();
  const show = inView || reduce;
  return (
    <div ref={ref} className="mt-auto pt-10">
      <motion.div
        initial={{ opacity: 0 }}
        animate={show ? { opacity: 1 } : {}}
        transition={{ duration: reduce ? 0 : 0.5, ease: easeOut }}
        className="flex items-center justify-between font-mono text-[10px] uppercase tracking-mono text-seabed-foreground/50"
      >
        <span>W1</span><span>W2</span><span>W3</span>
        <span>W4</span><span>W5</span><span>W6</span>
      </motion.div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={show ? { scaleX: 1 } : {}}
        transition={{ duration: reduce ? 0 : 0.6, ease: easeOut, delay: reduce ? 0 : 0.15 }}
        className="mt-2 h-px w-full bg-white/20 origin-left"
      />
      <div className="relative mt-4 space-y-4">
        {tasks.map((t, i) => {
          const delay = reduce ? 0 : 0.35 + i * 0.14;
          return (
            <div key={t.label} className="relative h-6">
              <motion.div
                initial={{ width: 0 }}
                animate={show ? { width: `${t.width}%` } : {}}
                transition={{ duration: reduce ? 0 : 0.65, ease: easeOut, delay }}
                className="absolute top-0 h-2"
                style={{
                  left: `${t.left}%`,
                  backgroundColor: "hsl(0 0% 100% / 0.28)",
                }}
              />
              <motion.span
                initial={{ opacity: 0 }}
                animate={show ? { opacity: 1 } : {}}
                transition={{ duration: reduce ? 0 : 0.35, ease: easeOut, delay: delay + 0.5 }}
                className="absolute top-3 font-mono text-[10px] uppercase tracking-[0.12em] text-seabed-foreground/70 whitespace-nowrap"
                style={{ left: `${t.left}%` }}
              >
                {t.label}
              </motion.span>
            </div>
          );
        })}
        {/* Playhead loop */}
        {!reduce && show && (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 w-px"
            style={{
              backgroundColor: "hsl(0 0% 100% / 0.5)",
              boxShadow: "0 0 4px hsl(0 0% 100% / 0.4)",
            }}
            initial={{ left: "0%", opacity: 0 }}
            animate={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 3.5,
              ease: "linear",
              times: [0, 0.08, 0.92, 1],
              delay: 1.6,
              repeat: Infinity,
              repeatDelay: 1.5,
            }}
          />
        )}
      </div>
    </div>
  );
};




const mono = "font-mono text-[10px] uppercase tracking-mono";

// 1. Strategy — horizontal milestone rail
const StrategyVisual = () => (
  <VisualFrame>
    <div className="relative w-[80%]">
      <div className="relative h-px bg-foreground/20" />
      <div className="absolute top-0 left-0 right-0 flex justify-between -translate-y-1/2">
        {["Plan", "Govern", "Scale"].map((label, i) => (
          <span
            key={label}
            className={`block size-2 rounded-full ${
              i === 2 ? "bg-primary" : "bg-foreground/40"
            }`}
          />
        ))}
      </div>
      <div className="mt-5 flex justify-between">
        {["Plan", "Govern", "Scale"].map((label, i) => (
          <span
            key={label}
            className={`${mono} ${
              i === 2 ? "text-foreground" : "text-foreground/55"
            }`}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  </VisualFrame>
);

// 2. Speed — clean progress bar with a single accent point
const SpeedVisual = () => (
  <VisualFrame>
    <div className="w-[80%]">
      <div className={`flex justify-between ${mono} text-foreground/55`}>
        <span>Contract</span>
        <span>Production</span>
      </div>
      <div className="relative mt-3 h-1.5 rounded-full bg-foreground/15 overflow-hidden">
        <div className="h-full w-[72%] rounded-full bg-foreground/55" />
      </div>
      <div className="relative mt-3">
        <div
          className="absolute -top-[22px] flex flex-col items-center"
          style={{ left: "72%", transform: "translateX(-50%)" }}
        >
          <span className="size-1.5 rounded-full bg-primary" />
        </div>
        <p className={`${mono} text-foreground/65 text-center`}>
          <span className="text-primary">&lt; 6 weeks</span> to first model live
        </p>
      </div>
    </div>
  </VisualFrame>
);

// 3. Embedded — simple stacked rows representing a shared table
const EmbeddedVisual = () => {
  const roles = ["Clinical", "IT", "Management", "Compliance", "Procurement"];
  return (
    <VisualFrame>
      <div className="w-[78%] space-y-1.5">
        {roles.map((r, i) => (
          <div
            key={r}
            className="flex items-center gap-3 border border-foreground/12 rounded-md px-3 py-1.5"
          >
            <span
              className={`size-1.5 rounded-full ${
                i === 0 ? "bg-primary" : "bg-foreground/35"
              }`}
            />
            <span className={`${mono} text-foreground/75`}>{r}</span>
          </div>
        ))}
      </div>
    </VisualFrame>
  );
};

// 4. Adoption — clean rising sparkline, single end marker
const AdoptionVisual = () => (
  <VisualFrame>
    <svg viewBox="0 0 200 80" className="w-[80%] h-auto" aria-hidden>
      <defs>
        <linearGradient id="adoptFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.25" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0,65 L30,58 L60,52 L95,42 L130,32 L165,20 L195,8 L195,80 L0,80 Z"
        fill="url(#adoptFill)"
      />
      <path
        d="M0,65 L30,58 L60,52 L95,42 L130,32 L165,20 L195,8"
        fill="none"
        stroke="hsl(var(--foreground) / 0.55)"
        strokeWidth="1.25"
      />
      <circle cx="195" cy="8" r="3" fill="hsl(var(--primary))" />
    </svg>
  </VisualFrame>
);

const visuals = [StrategyVisual, SpeedVisual, EmbeddedVisual, AdoptionVisual];

/* ---------------- Card ---------------- */

const BentoCard = ({
  row,
  i,
  className = "",
}: {
  row: Row;
  i: number;
  className?: string;
}) => {
  const Visual = visuals[i];
  return (
    <article
      className={`group relative flex flex-col rounded-card border border-border p-card-standard transition-colors hover:border-foreground/30 ${className}`}
      style={{ backgroundColor: "hsl(var(--seabed))" }}
    >
      <p className="eyebrow">
        0{i + 1} · {row.caption}
      </p>
      <div className="mt-6">
        <Visual />
      </div>
      <h3 className="display mt-7 text-[22px] md:text-[26px] leading-[1.15] text-foreground">
        {row.title}
      </h3>
      <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-muted-foreground">
        {row.body}
      </p>
    </article>
  );
};

/* ---------------- Section ---------------- */

const WhyDeepcBento = () => {
  // Asymmetric bento: tall/wide alternating — col-spans on a 12-col grid.
  const spans = [
    "lg:col-span-5",
    "lg:col-span-7",
    "lg:col-span-7",
    "lg:col-span-5",
  ];

  return (
    <section
      className="relative py-section-tight border-y border-border overflow-hidden"
      style={{
        ["--background" as any]: "var(--seabed)",
        ["--foreground" as any]: "var(--seabed-foreground)",
        ["--card" as any]: "var(--seabed-elevated)",
        ["--card-foreground" as any]: "var(--seabed-foreground)",
        ["--muted" as any]: "var(--seabed-deep)",
        ["--muted-foreground" as any]: "var(--seabed-muted)",
        ["--border" as any]: "var(--seabed-line)",
        backgroundColor: "hsl(var(--seabed))",
        color: "hsl(var(--seabed-foreground))",
      }}
    >
      {/* Dot grid background */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(var(--seabed-foreground) / 0.15) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div className="container relative">
        <Reveal className="max-w-3xl">
          <p className="eyebrow">Why leading health systems choose <Brand /></p>
          <h2 className="display mt-5 text-section-title text-foreground">
            A partnership built for the institutional reality of clinical AI.
          </h2>
        </Reveal>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Card 0 — Strategic Partnership: col 1-8, row 1 */}
          <article
            className="group relative flex flex-col rounded-card border border-border p-card-standard transition-colors hover:border-foreground/30 lg:col-span-8"
            style={{ backgroundColor: "hsl(var(--seabed))" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center h-full">
              <div className="lg:col-span-5">
                <p className="eyebrow">01 · {rows[0].caption}</p>
                <h3 className="display mt-7 text-[22px] md:text-[26px] leading-[1.15] text-foreground">
                  {rows[0].title}
                </h3>
                <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-muted-foreground">
                  {rows[0].body}
                </p>
              </div>
              <div className="lg:col-span-7">
                <PartnershipTimeline bare />
              </div>
            </div>
          </article>

          {/* Card 1 — Time-to-Value: col 9-12, row-span 2, PURPLE accent */}
          <article
            className="group relative flex flex-col rounded-card p-card-standard lg:col-span-4 lg:row-span-2 overflow-hidden"
            style={{
              backgroundColor: "hsl(var(--primary))",

              backgroundImage:
                "radial-gradient(circle at 100% 0%, hsl(0 0% 100% / 0.18), transparent 60%)",
            }}
          >
            <p className="font-mono text-[11px] uppercase tracking-mono text-seabed-foreground/60">
              02 / TIME-TO-VALUE
            </p>
            <h3
              className="display mt-7 text-[28px] md:text-[32px] leading-[1.15] text-seabed-foreground"
              style={{ fontWeight: 500 }}
            >
              From contract to production in{" "}
              <span style={{ fontWeight: 600 }}>&lt; 6 weeks</span>.
            </h3>
            <p className="mt-5 text-[14px] md:text-[15px] leading-relaxed text-seabed-foreground/75">
              Stress tested deployment patterns across leading academic centers,
              national health systems, and multi-site networks.
            </p>

            {/* Gantt-style task timeline reinforcing the 6-week message */}
            <GanttTimeline />

          </article>

          {/* Card 2 — Multidisciplinary: col 1-8, row 2 */}
          <article
            className="group relative flex flex-col rounded-card border border-border p-card-standard transition-colors hover:border-foreground/30 lg:col-span-8"
            style={{ backgroundColor: "hsl(var(--seabed))" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center h-full">
              <div className="lg:col-span-5">
                <p className="eyebrow">03 · {rows[2].caption}</p>
                <h3 className="display mt-7 text-[22px] md:text-[26px] leading-[1.15] text-foreground">
                  {rows[2].title}
                </h3>
                <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-muted-foreground">
                  {rows[2].body}
                </p>
              </div>
              <div className="lg:col-span-7">
                <CollaborationStack />
              </div>

            </div>
          </article>

          {/* Card 3 — Adoption + ROI: col 1-12, row 3, full width */}
          <article
            className="group relative flex flex-col rounded-card border border-border p-card-standard transition-colors hover:border-foreground/30 lg:col-span-12"
            style={{ backgroundColor: "hsl(var(--seabed))" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-4">
                <p className="eyebrow">04 · {rows[3].caption}</p>
                <h3 className="display mt-7 text-[22px] md:text-[26px] leading-[1.15] text-foreground">
                  {rows[3].title}
                </h3>
                <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-muted-foreground">
                  {rows[3].body}
                </p>
              </div>
              <div className="lg:col-span-8">
                <AdoptionCurve bare />
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default WhyDeepcBento;
